import type { Session, SessionStatus } from "@mux/sdk/v2/client"
import { unwrap } from "solid-js/store"
import { isCanonicalOracleSession } from "@/context/oracle/session-cache"
import { requestSurfaces } from "./request-index"
import { sessionRevision } from "./session-revision"
import type { GlobalStore, SidebarProjectionCache } from "./types"

export const SIDEBAR_STATUS_CACHE_MAX_AGE_MS = 15 * 60 * 1000

type CachedRunState = Pick<SidebarProjectionCache, "statuses" | "waiting" | "requests">

/** The cached run state and open requests to draw before the machine answers; none once too old to trust. */
export function cachedSidebarRunState(projection: SidebarProjectionCache | undefined, now = Date.now()): CachedRunState {
  if (!projection || now - projection.capturedAt > SIDEBAR_STATUS_CACHE_MAX_AGE_MS) {
    return { statuses: {}, waiting: {}, requests: { question: {}, permission: {}, request_route: {} } }
  }
  return { statuses: projection.statuses, waiting: projection.waiting, requests: projection.requests }
}

// Serialized through the store, so every field is read and tracked.
const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value))

/**
 * The tray state the store holds now, as the sidebar cache keeps it: run
 * statuses, waiting counts and open requests, with the records of the chats
 * they concern and of every workspace's Oracle, which the projection lists
 * and no other cache holds. Read in an effect, it tracks everything it
 * copies, a record through its revision (which every change to the chat's
 * row raises), so the effect can keep the cache current with each change
 * instead of only at snapshots.
 */
export function sidebarProjectionCache(
  store: Pick<GlobalStore, "session" | "session_status" | "session_waiting" | "question" | "permission" | "request_route">,
  capturedAt: number,
): SidebarProjectionCache {
  const statuses: Record<string, SessionStatus> = {}
  for (const [sessionID, status] of Object.entries(store.session_status)) {
    if (status && status.type !== "idle") statuses[sessionID] = copy(status)
  }
  const waiting: Record<string, number> = {}
  for (const [sessionID, launches] of Object.entries(store.session_waiting)) {
    if (launches && launches > 0) waiting[sessionID] = launches
  }
  const requests: SidebarProjectionCache["requests"] = { question: {}, permission: {}, request_route: {} }
  for (const [sessionID, list] of Object.entries(store.question)) {
    if (list?.length) requests.question[sessionID] = copy(list)
  }
  for (const [sessionID, list] of Object.entries(store.permission)) {
    if (list?.length) requests.permission[sessionID] = copy(list)
  }
  for (const [sessionID, route] of Object.entries(store.request_route)) {
    if (route) requests.request_route[sessionID] = copy(route)
  }
  const listed = new Set([...Object.keys(statuses), ...Object.keys(waiting)])
  for (const asker of [...Object.keys(requests.question), ...Object.keys(requests.permission)]) {
    for (const chat of requestSurfaces(requests.request_route, asker)) listed.add(chat)
  }
  const sessions: Session[] = []
  for (const session of store.session) {
    const oracle = !!session.workspaceID && isCanonicalOracleSession(session, session.workspaceID) && !session.time?.archived
    if (!oracle && !listed.has(session.id)) continue
    void sessionRevision(session)
    sessions.push(structuredClone(unwrap(session)))
  }
  return { capturedAt, sessions, statuses, waiting, requests }
}

/**
 * Of the Oracle records a page drew from its cache, those the machine's list
 * of every workspace's current Oracle leaves out and nothing has updated
 * since: copies of an Oracle replaced while the page was closed, which would
 * otherwise stay a second Oracle for their workspace.
 */
export function staleCachedOracleIDs(cached: readonly Session[], current: readonly Session[], store: readonly Session[]) {
  const listed = new Set(current.map((oracle) => oracle.id))
  const held = new Map(store.map((session) => [session.id, session]))
  const stale = new Set<string>()
  for (const copy of cached) {
    if (!copy.workspaceID || !isCanonicalOracleSession(copy, copy.workspaceID) || listed.has(copy.id)) continue
    const now = held.get(copy.id)
    if (now && sessionRevision(now) === sessionRevision(copy)) stale.add(copy.id)
  }
  return stale
}
